/**
 * Per-meter weekly anomaly report delivery prefs (dashboard-editable).
 * Collection: report_prefs/{meterId}
 */
import { deleteField, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

export const REPORT_PREFS_COLLECTION = 'report_prefs';

export const DEFAULT_REPORT_EMAIL = 'tinnisaha71@gmail.com';
/** Monday — matches previous Cloud Scheduler default. */
export const DEFAULT_REPORT_SEND_DAY = 1;
/** 08:00 Asia/Kolkata — matches previous Cloud Scheduler default. */
export const DEFAULT_REPORT_SEND_TIME = '08:00';
/** Max recipients per meter (also capped in Firestore rules). */
export const MAX_REPORT_EMAILS = 10;
/** RFC practical max length for an email address. */
export const MAX_EMAIL_LENGTH = 254;

export interface ReportPrefs {
  meterId: string;
  emails: string[];
  /** 0=Sun … 6=Sat (JS Date.getDay), Asia/Kolkata. */
  sendDay: number;
  /** "HH:mm" Asia/Kolkata. */
  sendTime: string;
  updatedAt: string;
}

export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Strict email check: single address, has @ and domain TLD, no spaces,
 * no consecutive dots, length within limits.
 */
export function isValidEmail(raw: string): boolean {
  return validateEmailInput(raw) === null;
}

/** Returns an error message, or null if the email is allowed. */
export function validateEmailInput(raw: string): string | null {
  const email = normalizeEmail(raw);
  if (!email) return 'Enter an email address.';
  if (/\s/.test(raw.trim())) return 'Email cannot contain spaces.';
  if (email.length > MAX_EMAIL_LENGTH) return `Email must be at most ${MAX_EMAIL_LENGTH} characters.`;
  if ((email.match(/@/g) || []).length !== 1) return 'Email must contain exactly one @.';
  if (email.startsWith('.') || email.endsWith('.') || email.includes('..')) {
    return 'Email format is invalid.';
  }
  // local@domain.tld — local/domain chars limited; TLD at least 2 letters
  if (!/^[a-z0-9](?:[a-z0-9._+-]*[a-z0-9])?@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i.test(email)) {
    return 'Enter a valid email like name@gmail.com.';
  }
  const domain = email.split('@')[1] || '';
  const tld = domain.split('.').pop() || '';
  if (tld.length < 2) return 'Enter a valid email like name@gmail.com.';
  return null;
}

export function normalizeSendTime(raw: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export async function loadReportPrefs(meterId: string): Promise<ReportPrefs | null> {
  const snap = await getDoc(doc(db, REPORT_PREFS_COLLECTION, meterId));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<ReportPrefs>;
  const emails = Array.isArray(data.emails)
    ? data.emails.map(normalizeEmail).filter(Boolean)
    : [];
  const sendTime = typeof data.sendTime === 'string'
    ? (normalizeSendTime(data.sendTime) || DEFAULT_REPORT_SEND_TIME)
    : DEFAULT_REPORT_SEND_TIME;
  const sendDay = typeof data.sendDay === 'number' && data.sendDay >= 0 && data.sendDay <= 6
    ? data.sendDay
    : DEFAULT_REPORT_SEND_DAY;
  return {
    meterId,
    emails,
    sendDay,
    sendTime,
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : '',
  };
}

export async function saveReportPrefs(entry: {
  meterId: string;
  emails: string[];
  sendDay: number;
  sendTime: string;
}): Promise<ReportPrefs> {
  const emails = [...new Set(entry.emails.map(normalizeEmail).filter(isValidEmail))];
  if (emails.length === 0) throw new Error('Add at least one valid email address.');
  if (emails.length > MAX_REPORT_EMAILS) {
    throw new Error(`You can add at most ${MAX_REPORT_EMAILS} emails per meter.`);
  }
  const sendTime = normalizeSendTime(entry.sendTime);
  if (!sendTime) throw new Error('Invalid send time');
  if (entry.sendDay < 0 || entry.sendDay > 6) throw new Error('Invalid send day');

  const payload: ReportPrefs = {
    meterId: entry.meterId,
    emails,
    sendDay: entry.sendDay,
    sendTime,
    updatedAt: new Date().toISOString(),
  };
  // Clearing sent markers so the newly saved day/time (or emails) can fire
  // on the next matching slot — not blocked by "already sent this week".
  await setDoc(
    doc(db, REPORT_PREFS_COLLECTION, entry.meterId),
    {
      ...payload,
      lastSentWeekStart: deleteField(),
      lastSentAt: deleteField(),
    },
    { merge: true },
  );
  return payload;
}

/** Allow the scheduler to send again this week after a refreshed softcopy. */
export async function clearReportSentMarker(meterId: string): Promise<void> {
  await setDoc(
    doc(db, REPORT_PREFS_COLLECTION, meterId),
    {
      lastSentWeekStart: deleteField(),
      lastSentAt: deleteField(),
    },
    { merge: true },
  );
}
